import { useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../fetchInterceptor';
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from './ui/card';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { ShieldCheck, FolderTree } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Workspace admin UI to grant/revoke `templates.create` to non-admin members.
 * Shown inside the Team tab of Settings. Visible only to owner/admin roles.
 */
export default function TemplatePermissionsCard({ workspaceId }) {
  const [grants, setGrants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);

  const fetchGrants = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/template-grants`);
      if (res.ok) {
        setGrants(await res.json());
        setError(null);
      } else if (res.status === 403) {
        setError('Only workspace owners and admins can manage template permissions.');
      } else {
        setError('Failed to load template permissions.');
      }
    } catch {
      setError('Failed to load template permissions.');
    } finally {
      setLoading(false);
    }
  }, [workspaceId]);

  useEffect(() => { fetchGrants(); }, [fetchGrants]);

  const toggle = async (member, newValue) => {
    if (member.has_role_perm) return; // owners/admins always have it
    setBusy(member.user_id);
    try {
      const res = await apiFetch(`${API_URL}/api/workspaces/${workspaceId}/template-grants`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: member.user_id, permission: 'templates.create', grant: newValue }),
      });
      if (res.ok) {
        toast.success(newValue ? `Granted template-create to ${member.name || member.email}` : `Revoked template-create from ${member.name || member.email}`);
        fetchGrants();
      } else {
        const e = await res.json().catch(() => ({}));
        toast.error(e.detail || 'Failed to update');
      }
    } catch { toast.error('Failed to update'); }
    finally { setBusy(null); }
  };

  if (error) return null; // hide silently if user is not an admin

  return (
    <Card data-testid="template-permissions-card">
      <CardHeader className="flex flex-row items-center gap-3">
        <ShieldCheck className="w-5 h-5 text-muted-foreground" />
        <div>
          <CardTitle className="flex items-center gap-2">
            <FolderTree className="w-4 h-4" /> Template Permissions
          </CardTitle>
          <CardDescription>
            Allow non-admin members to create and edit folder templates in this workspace.
          </CardDescription>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {loading ? (
          <div className="text-sm text-muted-foreground py-4">Loading…</div>
        ) : grants.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4">No members yet.</div>
        ) : (
          <div className="divide-y">
            {grants.map(m => (
              <div key={m.user_id} className="flex items-center justify-between py-3 gap-3" data-testid={`grant-row-${m.user_id}`}>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-medium text-sm truncate">{m.name || m.email}</span>
                    <Badge variant="outline" className="text-[9px] capitalize">{m.role}</Badge>
                    {m.has_role_perm && <Badge className="text-[9px] bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">Auto (role)</Badge>}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">{m.email}</div>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                  <span className="text-xs text-muted-foreground">{m.can_create_templates ? 'Allowed' : 'Blocked'}</span>
                  <Switch
                    checked={Boolean(m.can_create_templates)}
                    disabled={m.has_role_perm || busy === m.user_id}
                    onCheckedChange={(v) => toggle(m, v)}
                    data-testid={`grant-switch-${m.user_id}`}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
        <p className="text-[10px] text-muted-foreground pt-2">
          Owners and admins always have this permission. Toggle the switch to grant or revoke it for other roles (accountants, bookkeepers, etc.).
        </p>
      </CardContent>
    </Card>
  );
}
